import { useState } from "react";
import { PencilSimple, X } from "@phosphor-icons/react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import PinPromptModal from "./PinPromptModal";

const METHODS = [
  { id: "tyro_terminal", label: "Tyro (already charged on terminal)" },
  { id: "card_manual", label: "Card" },
  { id: "cash", label: "Cash" },
  { id: "other", label: "Other" },
];

export const canEditManualPayment = (p) => {
  const m = (p?.method || "").toLowerCase();
  if (p?.corrected_at || p?.is_refund || p?.tyro_meta || p?.source === "stripe" || Number(p?.amount) <= 0) return false;
  return ["cash", "card", "card_manual", "other"].includes(m) || (m === "tyro" && p?.tyro_offline);
};

export default function EditManualPaymentModal({ saleId, payment, maxAmount, onClose, onDone }) {
  const [amount, setAmount] = useState(String(maxAmount != null ? Math.min(Number(payment.amount), maxAmount) : payment.amount));
  const current = (payment.method || "").toLowerCase();
  const [method, setMethod] = useState(payment.tyro_offline ? "tyro_terminal" : current.startsWith("card") ? "card_manual" : (current || "other"));
  const [authCode, setAuthCode] = useState(payment.tyro_auth_code || "");
  const [reason, setReason] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const overMax = method !== "cash" && maxAmount != null && Number(amount) > maxAmount + 0.005;
  const valid = Number(amount) > 0 && reason.trim().length >= 3 && !overMax;

  const submit = async (pin) => {
    try {
      const { data } = await api.post(`/sales/${saleId}/payments/edit`, {
        payment_id: payment.payment_id || null,
        payment_index: payment.payment_index,
        amount: Number(amount),
        method,
        auth_code: method === "tyro_terminal" ? authCode.trim() : "",
        reason: reason.trim(),
      }, { headers: { "X-Admin-Pin": pin } });
      toast.success(`Payment updated to $${money(Number(amount))}`);
      onDone?.(data);
      onClose?.();
    } catch (error) {
      throw error;
    }
  };

  const input = "w-full h-11 px-3 rounded-xl border border-slate-200 text-[14px] text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none";
  return (
    <>
      <div className="fixed inset-0 z-[10010] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-payment-overlay">
        <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="edit-payment-modal">
          <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><PencilSimple size={19} weight="bold" /></div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-700">Edit recorded payment</div>
                <h3 className="mt-1 text-[18px] font-bold text-slate-900">{(payment.method || "").replace("_manual", "")} ${money(payment.amount)}</h3>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center" data-testid="edit-payment-close"><X size={15} weight="bold" /></button>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-[13px] leading-relaxed text-blue-900" data-testid="edit-payment-notice">
              Fixes the record only — it never charges, refunds or voids a card.
            </div>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Amount actually paid</span>
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={input} data-testid="edit-payment-amount" />
              {maxAmount != null && <span className={`block mt-1 text-[11px] ${overMax ? "text-rose-600 font-semibold" : "text-slate-500"}`} data-testid="edit-payment-max-hint">Sale total allows up to ${money(maxAmount)} for this entry (non-cash)</span>}
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Paid by</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={input} data-testid="edit-payment-method">
                {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
            {method === "tyro_terminal" && (
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Tyro auth code (optional)</span>
                <input value={authCode} onChange={(e) => setAuthCode(e.target.value)} placeholder="From the terminal receipt" className={input} data-testid="edit-payment-auth-code" />
              </label>
            )}
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Reason</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Charged $100 on Tyro; Other + $200 pressed by mistake" className="w-full min-h-20 px-3 py-2.5 rounded-xl border border-slate-200 text-[13px] focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none resize-y" data-testid="edit-payment-reason" />
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 text-[13px] font-semibold text-slate-700 hover:border-slate-400 transition-colors" data-testid="edit-payment-cancel">Cancel</button>
              <button onClick={() => setPinOpen(true)} disabled={!valid} className="flex-1 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-[13px] font-semibold transition-colors" data-testid="edit-payment-continue">Save with PIN</button>
            </div>
          </div>
        </div>
      </div>
      <PinPromptModal open={pinOpen} title="Authorize payment edit" description="Your admin PIN is required. The original amount and method stay in the audit history." confirmLabel="SAVE PAYMENT" statusEndpoint="/users/me/pin-status" zIndexClass="z-[10020]" onClose={() => setPinOpen(false)} onConfirm={submit} />
    </>
  );
}
